import { company } from '~/company/Company';
import { CompanyName } from '~/company/interface';
import type { PurchaseSnapshot } from '~/services/purchaseRecovery';
import {
  DOMAIN,
  REASON,
  reportError,
  reportWarning,
} from '~/services/telemetry';
import { mainStore, userStore } from '~/stores';

import { analyticsService } from './AnalyticsService';
import type { TikTokContent, TikTokFirstPurchaseData } from './events';
import { AnalyticsEvent } from './events';
import { resetDataLayerKeys } from './providers/GtmProvider';

/** Customer identifiers for `ttq.identify`, SHA-256 hex of normalised values */
type TikTokIdentity = Pick<
  TikTokFirstPurchaseData,
  'hashed_email' | 'hashed_phone' | 'hashed_external_id'
>;

const CONTENT_TYPE = 'product';
const DATA_LAYER_KEY = 'tiktok';

const isSupportedCompany = (): boolean =>
  company.match({ [CompanyName.CityDrinks]: true, default: false });

/**
 * SHA-256 hex digest via the native Web Crypto API.
 *
 * `crypto-js` is already a dependency but only reachable from the checkout
 * chunk, while this module sits in the entry graph (`~/stores` constructs
 * `CheckoutStore`, which imports `purchaseTracking`). Importing it here would
 * pull the vendor into the critical chunk for every page.
 *
 * Returns an empty string outside a secure context, where `crypto.subtle` is
 * undefined. Empty input is never hashed — the digest of `''` is a constant
 * that would merge every identity-less customer into one audience.
 */
const sha256Hex = async (value: string): Promise<string> => {
  if (!value || !globalThis.crypto?.subtle) return '';

  const bytes = new TextEncoder().encode(value);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);

  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
};

const normalizeEmail = (email: string): string => email.trim().toLowerCase();

/**
 * Strips a phone down to digits behind a leading `+`.
 *
 * Whether the result is valid E.164 depends on the stored number already
 * carrying a country code — `UserStore` keeps it without the `+`, and a locally
 * formatted number stays local here.
 */
const normalizePhone = (phone: string): string => {
  const digits = phone.replace(/\D/g, '');

  return digits ? `+${digits}` : '';
};

const buildIdentity = async (): Promise<TikTokIdentity> => {
  const [hashedEmail, hashedPhone, hashedExternalId] = await Promise.all([
    sha256Hex(normalizeEmail(userStore.email)),
    sha256Hex(normalizePhone(userStore.phone)),
    sha256Hex(userStore.personalData.id ?? ''),
  ]);

  return {
    hashed_email: hashedEmail,
    hashed_phone: hashedPhone,
    hashed_external_id: hashedExternalId,
  };
};

/** Price actually paid per unit — `item.price` is the pre-discount value */
const paidPrice = (price: number, discount: number): number =>
  discount ? Math.max(price - discount, 0) : price;

const buildContents = (snapshot: PurchaseSnapshot): TikTokContent[] =>
  snapshot.items.map((item) => ({
    content_id: item.item_id,
    content_type: CONTENT_TYPE,
    content_name: item.item_name,
    price: paidPrice(item.price, item.discount),
    quantity: item.quantity,
  }));

/**
 * Warns when the event goes out without any identifier.
 *
 * The snapshot is marked as sent before `trackPurchase` runs, so an event that
 * leaves without identifiers can never be replayed — without this the loss is
 * invisible.
 */
const reportMissingIdentity = (
  identity: TikTokIdentity,
  orderId: string,
): void => {
  if (identity.hashed_email || identity.hashed_phone) return;

  reportWarning(DOMAIN.analytics, 'TikTok identity unavailable', {
    reason: REASON.analytics.tiktokIdentityMissing,
    extras: {
      orderId,
      hasSubtle: Boolean(globalThis.crypto?.subtle),
      hasExternalId: Boolean(identity.hashed_external_id),
    },
  });
};

/**
 * Pushes the first-purchase payload TikTok's GTM tag reads.
 *
 * Deliberately a separate dataLayer event rather than extra keys on
 * `FIRST_PURCHASE`: that one also routes to Firebase, and the hashed
 * identifiers must not reach GA4.
 *
 * Never rejects — the caller sits on the path that runs right after an order is
 * paid for, where an unhandled rejection would surface as a Sentry alert.
 */
export const trackTikTokFirstPurchase = async (
  snapshot: PurchaseSnapshot,
): Promise<void> => {
  if (!isSupportedCompany()) return;

  try {
    const contents = buildContents(snapshot);
    const [firstContent] = contents;
    const identity = await buildIdentity();

    reportMissingIdentity(identity, snapshot.orderId);

    analyticsService.track(AnalyticsEvent.TIKTOK_FIRST_PURCHASE, {
      tiktok: {
        ...identity,
        order_id: snapshot.orderId,
        contents,
        content_id: firstContent?.content_id ?? '',
        content_type: CONTENT_TYPE,
        content_name: firstContent?.content_name ?? '',
        content_price: firstContent?.price ?? 0,
        content_quantity: firstContent?.quantity ?? 0,
        value: mainStore.toFloat(snapshot.finalPrice),
        currency: snapshot.currency.toUpperCase(),
      },
    });

    resetDataLayerKeys([DATA_LAYER_KEY]);
  } catch (error) {
    reportError(DOMAIN.analytics, error, {
      reason: REASON.analytics.tiktokTrackFailed,
      level: 'warning',
      extras: { orderId: snapshot.orderId },
    });
  }
};
